import { useMemo, useState } from "react";
import Papa from "papaparse";

const styles = {
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 14,
    background: "#fff",
    boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
    marginTop: 12,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  title: { fontSize: 14, fontWeight: 700, color: "#111827" },
  meta: { fontSize: 12, color: "#6b7280" },
  btn: {
    border: "1px solid #d1d5db",
    background: "#f9fafb",
    color: "#111827",
    borderRadius: 10,
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
  btnPrimary: {
    border: "1px solid #c7d2fe",
    background: "#eef2ff",
  },
  error: {
    marginTop: 10,
    padding: 10,
    borderRadius: 10,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    fontSize: 13,
  },
  tableWrap: {
    marginTop: 12,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    maxWidth: "100%",
    overflow: "visible",
  },
  scroll: {
    maxHeight: 360,
    width: "100%",
    overflowX: "auto",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",

  },
  table: {
    width: "max-content",
    minWidth: "max-content",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 13,
  },
  th: {
    position: "sticky",
    top: 0,
    background: "#f3f4f6",
    color: "#111827",
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    zIndex: 1,
  },
  td: {
    padding: "9px 12px",
    borderBottom: "1px solid #f1f5f9",
    maxWidth: 200,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  trEven: { background: "#ffffff" },
  trOdd: { background: "#fcfcfd" },
};

function decodeBase64Csv(base64Data) {
  const clean = (base64Data || "").replace(/^data:text\/csv;base64,/, "");
  const binary = atob(clean);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder("utf-8").decode(bytes);
}
function downloadCsv(base64Data, filename = "data.csv") {
  const clean = base64Data.replace(/^data:text\/csv;base64,/, "");
  const binary = atob(clean);
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
  const csvText = new TextDecoder("utf-8").decode(bytes);

  const blob = new Blob(
    ["\uFEFF" + csvText],
    { type: "text/csv;charset=utf-8;" }
  );

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function CsvViewer({ base64Data, title = "Detail data" }) {
  const [show, setShow] = useState(false);

  const { error, headers, rows } = useMemo(() => {
    try {
      if (!base64Data) return { error: null, headers: [], rows: [] };

      const decoded = decodeBase64Csv(base64Data);

      const parsed = Papa.parse(decoded, {
        header: false,
        skipEmptyLines: true,
      });

      if (parsed.errors?.length) {
        return { error: parsed.errors[0].message, headers: [], rows: [] };
      }

      const data = parsed.data || [];
      const hdr = data[0] || [];
      const body = data.slice(1);

      return { error: null, headers: hdr, rows: body };
    } catch (e) {
      return { error: e?.message || "CSV decode failed", headers: [], rows: [] };
    }
  }, [base64Data]);

  const rowCount = rows.length;
  const colCount = headers.length;

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.title}>{title}</div>
          <div style={styles.meta}>
            {colCount} columns · {rowCount} rows
          </div>
        </div>

        <button
          onClick={() => setShow((v) => !v)}
          style={{ ...styles.btn, ...(show ? styles.btnPrimary : null) }}
        >
          {show ? "Hide table ▲" : "Show table ▼"}
        </button>
        <button
          onClick={() =>
            downloadCsv(base64Data, `${title.replace(/\s+/g, "_")}.csv`)
          }
          style={styles.btn}
        >
          ⬇ Download CSV
        </button>
      </div>

      {error && <div style={styles.error}>CSV 解析錯誤：{error}</div>}

      {show && !error && (
        <div style={styles.tableWrap}>
          <div style={styles.scroll}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {headers.map((h, idx) => (
                    <th key={idx} style={styles.th} title={String(h)}>
                      {String(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={i % 2 ? styles.trOdd : styles.trEven}>
                    {headers.map((_, j) => {
                      const cell = r?.[j] ?? "";
                      return (
                        <td key={j} style={styles.td} title={String(cell)}>
                          {String(cell)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
